/**
 * Pinia store for schedule data
 * Global state management for schedule/filter functionality
 */

import { defineStore } from "pinia";
import {
  FILTER_SPLIT_CHAR,
  FILTER_NULL_VALUE,
  FILTER_PARAM_ORDER,
} from "../composables/constants.js";

// Module-level reference for hashchange handler (needed for cleanup)
let hashChangeHandler = null;

export const useScheduleStore = defineStore("schedule", {
  state: () => ({
    // Configuration data from API
    config: null,

    // Filter options from API
    filters: {
      series: [],
      date: [],
      release: [],
      cinema: [],
      auditorium: [],
      fsk: [],
    },

    // Current schedule view (initialized from backend via setConfig)
    scheduleView: null,

    // Active filter values
    activeFilters: {
      series: [],
      date: [],
      release: [],
      cinema: [],
      auditorium: [],
      fsk: [],
    },

    // Films data
    films: [],

    // Loading state
    loading: false,

    // Cinema city ID
    cityId: null,
  }),

  getters: {
    // Get filter values by key
    getFilterValues: (state) => (filterKey) => {
      return state.filters[filterKey] || null;
    },

    // Get config value by key
    getConfigValue: (state) => (key) => {
      return state.config?.[key] ?? null;
    },

    // Get active filter values by key
    getActiveFilterValues: (state) => (filterKey) => {
      return state.activeFilters[filterKey] || [];
    },

    // Check if filter has values
    hasFilterValues: (state) => (filterKey) => {
      const values = state.filters[filterKey];
      if (!values) return false;
      if (Array.isArray(values)) return values.length > 0;
      if (typeof values === "object") return Object.keys(values).length > 0;
      return false;
    },

    // Get current active filters as object (only non-empty filters)
    getCurrentFilters: (state) => {
      const filterParams = {};
      FILTER_PARAM_ORDER.forEach((filterKey) => {
        const activeFilters = state.activeFilters[filterKey] || [];
        if (activeFilters.length > 0) {
          filterParams[filterKey] = activeFilters;
        }
      });
      return filterParams;
    },
  },

  actions: {
    // Set configuration from API
    setConfig(configData) {
      if (configData.config) {
        this.config = configData.config;
      }
      if (configData.filters) {
        this.filters = { ...this.filters, ...configData.filters };
      }
      if (configData.scheduleView) {
        this.scheduleView =
          configData.scheduleView === "standard" ? "calendar" : configData.scheduleView;
      }
    },

    // Set films
    setFilms(films) {
      this.films = films;
    },

    // Set loading state
    setLoading(loading) {
      this.loading = loading;
    },

    // Set schedule view
    setScheduleView(view) {
      this.scheduleView = view;
    },

    // Set active filter
    setActiveFilter(filterType, values) {
      if (this.activeFilters[filterType] !== undefined) {
        this.activeFilters[filterType] = Array.isArray(values) ? values : [values];
      }
    },

    // Toggle filter value
    toggleFilter(filterType, value) {
      if (this.activeFilters[filterType] === undefined) {
        return;
      }

      const index = this.activeFilters[filterType].indexOf(value);
      if (index > -1) {
        this.activeFilters[filterType].splice(index, 1);
      } else {
        this.activeFilters[filterType].push(value);
      }
    },

    // Clear all filters
    clearAllFilters() {
      Object.keys(this.activeFilters).forEach((key) => {
        this.activeFilters[key] = [];
      });
    },

    // Set city ID
    setCityId(cityId) {
      this.cityId = cityId;
    },

    /**
     * Initialize filters from URL hash
     * Supports partial URLs with fewer than 6 params
     * @returns {boolean} true if filters were found in URL
     */
    initializeFiltersFromUrl() {
      const hashParams = new URL(window.location).hash;
      const filterStringStart = "#filter/";

      if (hashParams.includes(filterStringStart)) {
        const hashParamValues = hashParams.replace(filterStringStart, "").split("/");

        // Set as many filters as possible from available URL params (at least 1)
        if (hashParamValues.length >= 1) {
          FILTER_PARAM_ORDER.forEach((filterKey, paramIndex) => {
            // Only process filters whose paramIndex is within available URL params
            if (paramIndex < hashParamValues.length) {
              let paramValue = hashParamValues[paramIndex];

              if (paramValue && paramValue !== FILTER_NULL_VALUE) {
                // Decode URL-encoded values (handles spaces, commas, etc.)
                // This is necessary because values like "OV, 3D" get encoded as "OV,%203D" in the URL
                try {
                  paramValue = decodeURIComponent(paramValue);
                } catch (e) {
                  // If decoding fails (invalid encoding), use the original value
                  // This handles edge cases where the value might not be properly encoded
                  console.warn(
                    `Failed to decode filter parameter for ${filterKey}:`,
                    paramValue,
                    e,
                  );
                }

                // Split by FILTER_SPLIT_CHAR and filter out empty strings
                const filterValues = paramValue
                  .split(FILTER_SPLIT_CHAR)
                  .map((v) => v.trim())
                  .filter((v) => v.length > 0);

                if (filterValues.length > 0) {
                  this.setActiveFilter(filterKey, filterValues);
                }
              }
            }
          });
        }
        return true; // Filters were found in URL
      }
      return false; // No URL filters found
    },

    /**
     * Sync current filter state to URL hash
     * @param {string} basePath - Base path for URL (default: /programm)
     */
    syncFiltersToUrl(basePath = "/programm") {
      let hasValues = false;
      let filterString = "";

      const filterValues = FILTER_PARAM_ORDER.map((filterKey) => {
        const activeFilters = this.activeFilters[filterKey] || [];
        const filterValue = activeFilters.join(FILTER_SPLIT_CHAR);

        if (filterValue && filterValue.length > 0 && filterValue !== FILTER_NULL_VALUE) {
          hasValues = true;
          return filterValue;
        }

        return FILTER_NULL_VALUE;
      });

      if (hasValues) {
        filterString = `#filter/${filterValues.join("/")}`;
      }

      window.history.pushState({}, "", `${basePath}${filterString}`);
    },

    /**
     * Reset all filters and clear URL
     * @param {string} basePath - Base path for URL (default: /programm)
     */
    resetFiltersAndUrl(basePath = "/programm") {
      this.clearAllFilters();
      window.history.pushState({}, "", basePath);
    },

    /**
     * Dispatch filter change event for other components (e.g., MovieListing)
     */
    dispatchFilterChange() {
      window.dispatchEvent(
        new CustomEvent("vue-filterbar-values-changed", {
          detail: {
            filters: this.getCurrentFilters,
          },
        }),
      );
    },

    /**
     * Start watching for URL hash changes (browser back/forward navigation)
     * Call this on component mount
     */
    startWatchingHashChanges() {
      if (hashChangeHandler) return; // Already watching

      hashChangeHandler = () => {
        this.clearAllFilters();
        this.initializeFiltersFromUrl();
        this.dispatchFilterChange();

        // Focus on movie listing container for accessibility
        const movieListingEl = document.getElementById("movielisting");
        if (movieListingEl) {
          // Ensure element is focusable
          if (!movieListingEl.hasAttribute("tabindex")) {
            movieListingEl.setAttribute("tabindex", "-1");
          }
          movieListingEl.focus();
        }
      };

      window.addEventListener("hashchange", hashChangeHandler);
    },

    /**
     * Stop watching for URL hash changes
     * Call this on component unmount
     */
    stopWatchingHashChanges() {
      if (hashChangeHandler) {
        window.removeEventListener("hashchange", hashChangeHandler);
        hashChangeHandler = null;
      }
    },
  },
});
